/**
 * Work-order and visit clocks (#433). The office is America/Toronto. A UTC browser must show and
 * submit the same wall time Clayton typed, so display and datetime-local conversion are a pair.
 */

export const OFFICE_TZ = 'America/Toronto'

const officeDay = new Intl.DateTimeFormat('en-CA', { month: 'short', day: 'numeric', timeZone: OFFICE_TZ })
const officeStamp = new Intl.DateTimeFormat('en-CA', {
  month: 'short',
  day: 'numeric',
  hour: 'numeric',
  minute: '2-digit',
  timeZone: OFFICE_TZ,
})

const WALL = /^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})/

function pad(value: number): string {
  return String(value).padStart(2, '0')
}

/** ICU uses a narrow no-break space before "a.m." Keep a normal space so the clock is stable. */
function tidy(text: string): string {
  return text.replaceAll('\u00a0', ' ').replaceAll('\u202f', ' ')
}

function wallParts(instant: Date) {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: OFFICE_TZ,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(instant)
  const read = (type: string) => {
    const raw = parts.find((part) => part.type === type)?.value ?? '0'
    return Number(raw === '24' ? '0' : raw)
  }
  return {
    year: read('year'),
    month: read('month'),
    day: read('day'),
    hour: read('hour'),
    minute: read('minute'),
    second: read('second'),
  }
}

/** Minutes east of UTC for Toronto at this instant (EDT is -240, EST is -300). */
function officeOffsetMinutes(instant: Date): number {
  const wall = wallParts(instant)
  const asUtc = Date.UTC(wall.year, wall.month - 1, wall.day, wall.hour, wall.minute, wall.second)
  return Math.round((asUtc - instant.getTime()) / 60_000)
}

/** "Oct 14, 11:30 a.m." in America/Toronto. Empty or unparseable input is "Window not supplied". */
export function formatOfficeDateTime(value?: string | null): string {
  if (!value) return 'Window not supplied'
  const date = new Date(value)
  if (Number.isNaN(date.valueOf())) return 'Window not supplied'
  return tidy(officeStamp.format(date))
}

/** A scheduled window. Morning, afternoon and all-day keep the day and drop the clock. */
export function formatOfficeWindow(value?: string | null, precision?: string | null): string {
  if (!value) return 'Window not supplied'
  const date = new Date(value)
  if (Number.isNaN(date.valueOf())) return 'Window not supplied'
  const day = tidy(officeDay.format(date))
  if (precision === 'morning') return `${day} morning`
  if (precision === 'afternoon') return `${day} afternoon`
  if (precision === 'allday' || precision === 'all_day') return `${day} all day`
  return formatOfficeDateTime(value)
}

/** `YYYY-MM-DD` for this instant in America/Toronto. */
export function officeCalendarDay(now = new Date()): string {
  const wall = wallParts(now)
  return `${wall.year}-${pad(wall.month)}-${pad(wall.day)}`
}

/** UTC instant → `YYYY-MM-DDTHH:mm` wall time in America/Toronto, for a datetime-local input. */
export function officeDateTimeLocal(value?: string | null): string {
  if (!value) return ''
  const date = new Date(value)
  if (Number.isNaN(date.valueOf())) return ''
  const wall = wallParts(date)
  return `${wall.year}-${pad(wall.month)}-${pad(wall.day)}T${pad(wall.hour)}:${pad(wall.minute)}`
}

/**
 * `YYYY-MM-DDTHH:mm` typed as America/Toronto → UTC ISO.
 * One extra offset pass covers the DST fold. A gap (the missing spring hour) lands on the following offset.
 */
export function officeInstant(value: string): string | null {
  const match = WALL.exec(value.trim())
  if (!match) return null
  const year = Number(match[1])
  const month = Number(match[2])
  const day = Number(match[3])
  const hour = Number(match[4])
  const minute = Number(match[5])
  if (month < 1 || month > 12 || day < 1 || day > 31 || hour > 23 || minute > 59) return null
  const guess = Date.UTC(year, month - 1, day, hour, minute)
  const offset = officeOffsetMinutes(new Date(guess))
  let utc = guess - offset * 60_000
  const corrected = officeOffsetMinutes(new Date(utc))
  if (corrected !== offset) utc = guess - corrected * 60_000
  const date = new Date(utc)
  return Number.isNaN(date.valueOf()) ? null : date.toISOString()
}
